import { createAsyncThunk } from "@reduxjs/toolkit";
import Cookie from "universal-cookie";
import axios from "axios";
import { RAZZLE_DB_LINK } from "../../constants";
import {
  Articles,
  Creator,
  Vacancy,
  Categories,
  Tags,
  CaseStudyCategories,
} from "./types";

export const cookies = new Cookie();

let config = {
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
    Authorization: `Bearer ${
      typeof window !== "undefined" ? cookies.get("auth_token") : null
    }`,
  },
};

export const loginUser = async (username: string, password: string) => {
  try {
    let URL = `${RAZZLE_DB_LINK}/@login`;
    const { data } = await axios.post(
      URL,
      { login: username, password },
      config
    );
    cookies.set("auth_token", data.token, { path: "/" });
    window.location.reload(true);
    return data;
  } catch (err) {
    return err;
  }
};

export const getCategories = createAsyncThunk(
  "GET_CATEGORIES",
  async (_, { rejectWithValue }) => {
    try {
      let URL = `${RAZZLE_DB_LINK}/@search?portal_type=Folder&path.depth=1`;
      const { data } = await axios.get(URL, config);
      return data as Categories;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getTags = createAsyncThunk(
  "GET_TAGS",
  async (_, { rejectWithValue }) => {
    try {
      let URL = `${RAZZLE_DB_LINK}/get_unique_subjects`;
      const { data } = await axios.get(URL);
      return data as Tags;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getCaseStudyCategories = createAsyncThunk(
  "GET_CASE_STUDY_CATEGORIES",
  async (_, { rejectWithValue }) => {
    try {
      let URL = `${RAZZLE_DB_LINK}/get_unique_macrocategories`;
      const { data } = await axios.get(URL);
      return data as CaseStudyCategories;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getTotalArticles = createAsyncThunk(
  "GET_ARTICLES_TAGS",
  async (_, { rejectWithValue }) => {
    try {
      let URL = `${RAZZLE_DB_LINK}/@search?portal_type=News+Item&sort_on=effective&sort_order=reverse&fullobjects=0&b_size=60`;
      const { data } = await axios.get(URL, config);
      return data as Articles;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getSingleArticle = createAsyncThunk(
  "GET_SINGLE_ARTICLE",
  async (id: string, { rejectWithValue }) => {
    try {
      let URL = `${RAZZLE_DB_LINK}/@search?portal_type=News+Item&id=${id}&fullobjects=1&metadata_fields=Subject&metadata_fields=effective`;
      const { data } = await axios.get(URL, config);
      return data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const fetchArticles = createAsyncThunk(
  "FETCH_ARTICLES",
  async (articlesPerPage: number, { rejectWithValue }) => {
    try {
      let URL = `${RAZZLE_DB_LINK}/@search?portal_type=News+Item&sort_on=effective&sort_order=reverse&fullobjects=1&b_size=${articlesPerPage}`;
      const { data } = await axios.get(URL, config);
      return data as Articles;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getArticlesByCategory = createAsyncThunk(
  "GET_ARTICLES_BY_CATEGORY",
  async (
    {
      category,
      articlesPerPage,
    }: { category: string; articlesPerPage: number },
    { rejectWithValue }
  ) => {
    try {
      let URL = `${RAZZLE_DB_LINK}/${category}/@search?portal_type=News+Item&sort_on=effective&sort_order=reverse&fullobjects=1&b_size=${articlesPerPage}`;
      const { data } = await axios.get(URL, config);
      return data as Articles;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);
export const getArticlesByCategoryCaseStudy = createAsyncThunk(
  "GET_ARTICLES_BY_CATEGORY_CASE_STUDY",
  async (
    {
      category,
      page = 0,
      articlesPerPage,
    }: { category: string; page?: number; articlesPerPage: number },
    { rejectWithValue }
  ) => {
    try {
      const b_start = page * articlesPerPage;
      const URL =
        `${RAZZLE_DB_LINK}/${category}/@search?portal_type=News+Item` +
        `&sort_on=effective&sort_order=reverse&fullobjects=1` +
        `&b_start=${b_start}&b_size=${articlesPerPage}`;
      const { data } = await axios.get(URL, config);
      return data as Articles;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getArticlesByTag = createAsyncThunk(
  "GET_ARTICLES_BY_TAG",
  async (
    { tag, articlesPerPage }: { tag: string; articlesPerPage: number },
    { rejectWithValue }
  ) => {
    try {
      let URL = `${RAZZLE_DB_LINK}/@search?Subject=${tag}&portal_type=News+Item&sort_on=effective&sort_order=reverse&fullobjects=1&b_size=${articlesPerPage}`;
      const { data } = await axios.get(URL, config);
      return data as Articles;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getArticlesByCaseStudyCategory = createAsyncThunk(
  "GET_ARTICLES_BY_CASE_STUDY_CATEGORY",
  async (
    {
      category,
      page = 0,
      articlesPerPage,
    }: { category: string; page?: number; articlesPerPage: number },
    { rejectWithValue }
  ) => {
    try {
      const b_start = page * articlesPerPage;
      const URL =
        `${RAZZLE_DB_LINK}/@search?Category=${encodeURIComponent(
          category
        )}&portal_type=News+Item` +
        `&sort_on=effective&sort_order=reverse&fullobjects=1` +
        `&b_start=${b_start}&b_size=${articlesPerPage}`;
      const { data } = await axios.get(URL, config);
      return data as Articles;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const changePage = createAsyncThunk(
  "CHANGE_PAGE",
  async (
    { page, articlesPerPage }: { page: number; articlesPerPage: number },
    { rejectWithValue }
  ) => {
    try {
      const URL = `${RAZZLE_DB_LINK}/@search?portal_type=News+Item&sort_on=effective&sort_order=reverse&fullobjects=1&b_start=${
        page * articlesPerPage
      }&b_size=${articlesPerPage}`;
      const { data } = await axios.get(URL, config);
      return data as Articles;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const changeSubjectPage = createAsyncThunk(
  "CHANGE_SUBJECT_PAGE",
  async (
    {
      category,
      page,
      articlesPerPage,
    }: { category: string; page: number; articlesPerPage: number },
    { rejectWithValue }
  ) => {
    try {
      const URL = `${RAZZLE_DB_LINK}/${category}/@search?portal_type=News+Item&sort_on=effective&sort_order=reverse&fullobjects=1&b_start=${
        page * articlesPerPage
      }&b_size=${articlesPerPage}`;
      const { data } = await axios.get(URL, config);
      return data as Articles;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const changeTagPage = createAsyncThunk(
  "CHANGE_TAG_PAGE",
  async (
    {
      tag,
      page,
      articlesPerPage,
    }: { tag: string; page: number; articlesPerPage: number },
    { rejectWithValue }
  ) => {
    try {
      const URL = `${RAZZLE_DB_LINK}/@search?Subject=${tag}&portal_type=News+Item&sort_on=effective&sort_order=reverse&fullobjects=1&b_start=${
        page * articlesPerPage
      }&b_size=${articlesPerPage}`;
      const { data } = await axios.get(URL, config);
      return data as Articles;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const searchArticles = createAsyncThunk(
  "SEARCH_ARTICLES",
  async (text: string, { rejectWithValue }) => {
    try {
      const URL = `${RAZZLE_DB_LINK}/@search?SearchableText=${text}&portal_type=News+Item&sort_on=effective&sort_order=reverse&fullobjects=1&b_size=60`;
      const { data } = await axios.get(URL, config);
      return data as Articles;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getCreatorInfo = createAsyncThunk(
  "GET_CREATOR_INFO",
  async (creator: string, { rejectWithValue }) => {
    try {
      const URL = `${RAZZLE_DB_LINK}/get_user_info/${creator}`;
      const { data } = await axios.get(URL);
      return data as Creator;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getVacancies = createAsyncThunk(
  "GET_VACANCIES",
  async (_, { rejectWithValue }) => {
    try {
      const URL = `${RAZZLE_DB_LINK}/@search?portal_type=vacancy&fullobjects=1`;
      const { data } = await axios.get(URL, config);
      return data as Vacancy;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);
